import { sym } from '#library/symbol.library.js';
import { getType, protoType } from '#library/type.library.js';
import { isJSON, isRawJSON } from '#library/json.library.js';
export { isJSON, isRawJSON };
/**
 * Asserts if a value matches one of the provided types from the Type system.
 *
 * @param obj - The value to check
 * @param types - The list of valid Types
 * @returns True if the value matches one of the specified types
 * @example
 * ```ts
 * if (isType<string>(value, 'String', 'Number')) { ... }
 * ```
 */
export const isType = (obj, ...types) => types.includes(getType(obj));
/** Type-Guards: assert `<obj>` is of `<type>` */
export const isPrimitive = (obj) => isType(obj, 'String', 'Number', 'BigInt', 'Boolean', 'Symbol', 'Undefined', 'Void', 'Null', 'Empty');
export const isReference = (obj) => !isPrimitive(obj);
export const isIterable = (obj) => Symbol.iterator in Object(obj) && !isString(obj);
export const isString = (obj) => isType(obj, 'String');
export const isText = (obj) => isString(obj) && obj.trim().length > 0;
export const isNumber = (obj) => Number.isFinite(obj);
const RE_BIGINT_LITERAL = /^[+-]?[0-9]+n$/;
const RE_REGEXP_LITERAL = /^\/.*\/$/;
/**
 * Tests if a value can be safely converted to a numeric value.
 * Handles strings, numbers, and BigInts, verifying finite properties and valid formats.
 *
 * @param str - The value to test
 * @returns True if the value can be evaluated numerically
 * @example
 * ```ts
 * isNumeric('123'); // true
 * isNumeric('abc'); // false
 * ```
 */
export function isNumeric(str) {
    const type = typeof str;
    switch (type) {
        case 'number': return Number.isFinite(str);
        case 'bigint': return true;
        case 'string': {
            const val = str.trim();
            if (val.length === 0)
                return false;
            return RE_BIGINT_LITERAL.test(val) || isNumber(Number(val));
        }
        default: return false;
    }
}
export const isInteger = (obj) => isType(obj, 'BigInt');
export const isIntegerLike = (obj) => isType(obj, 'String') && RE_BIGINT_LITERAL.test(obj.trim());
export const isDigit = (obj) => isType(obj, 'Number', 'BigInt');
export const isBoolean = (obj) => isType(obj, 'Boolean');
export const isArray = (obj) => isType(obj, 'Array');
export const isArrayLike = (obj) => protoType(obj) === 'Object' && 'length' in obj && Object.keys(obj).every(key => key === 'length' || isNumber(Number(key)));
export const isObject = (obj) => isType(obj, 'Object');
export const isPlainObject = (obj) => {
    if (obj === null || typeof obj !== 'object')
        return false;
    const proto = Object.getPrototypeOf(obj);
    return proto === Object.prototype || proto === null;
};
export const isDate = (obj) => isType(obj, 'Date');
export const isRegExp = (obj) => isType(obj, 'RegExp');
export const isRegExpLike = (obj) => isType(obj, 'String') && RE_REGEXP_LITERAL.test(obj);
export const isSymbol = (obj) => isType(obj, 'Symbol');
export const isSymbolFor = (obj) => isType(obj, 'Symbol') && Symbol.keyFor(obj) !== undefined;
export const isPropertyKey = (obj) => isType(obj, 'String', 'Number', 'Symbol');
export const isNull = (obj) => isType(obj, 'Null');
export const isNullish = (obj) => isType(obj, 'Null', 'Undefined', 'Void', 'Empty');
export const isUndefined = (obj) => isType(obj, 'Undefined', 'Void', 'Empty');
export const isDefined = (obj) => !isNullish(obj);
export const isClass = (obj) => isType(obj, 'Class');
export const isFunction = (obj) => isType(obj, 'Function', 'AsyncFunction');
export const isPromise = (obj) => isType(obj, 'Promise');
export const isMap = (obj) => isType(obj, 'Map');
export const isSet = (obj) => isType(obj, 'Set');
export const isError = (err, constructor) => isType(err, 'Error') && (constructor ? err instanceof constructor : true);
export const isTemporal = (obj) => protoType(obj).startsWith('Temporal.') || (isDefined(globalThis.Temporal) && (obj instanceof globalThis.Temporal.Instant ||
    obj instanceof globalThis.Temporal.ZonedDateTime ||
    obj instanceof globalThis.Temporal.PlainDate ||
    obj instanceof globalThis.Temporal.PlainTime ||
    obj instanceof globalThis.Temporal.PlainDateTime ||
    obj instanceof globalThis.Temporal.Duration ||
    obj instanceof globalThis.Temporal.PlainYearMonth ||
    obj instanceof globalThis.Temporal.PlainMonthDay));
export const isInstant = (obj) => isType(obj, 'Temporal.Instant') || (isDefined(globalThis.Temporal?.Instant) && obj instanceof globalThis.Temporal.Instant) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.Instant') || (isDefined(obj) && isFunction(obj.toZonedDateTimeISO) && isUndefined(obj.timeZoneId) && isUndefined(obj.timeZone));
export const isZonedDateTime = (obj) => isType(obj, 'Temporal.ZonedDateTime') || (isDefined(globalThis.Temporal?.ZonedDateTime) && obj instanceof globalThis.Temporal.ZonedDateTime) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.ZonedDateTime') || (isDefined(obj) && isFunction(obj.toInstant) && (isDefined(obj.timeZoneId) || isDefined(obj.timeZone)));
export const isPlainDate = (obj) => isType(obj, 'Temporal.PlainDate') || (isDefined(globalThis.Temporal?.PlainDate) && obj instanceof globalThis.Temporal.PlainDate) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.PlainDate') || (isDefined(obj) && isFunction(obj.toZonedDateTime) && isUndefined(obj.timeZoneId) && isUndefined(obj.timeZone) && isDefined(obj.daysInMonth) && isUndefined(obj.hour) && isUndefined(obj.minute) && isUndefined(obj.second) && isUndefined(obj.nanosecond));
export const isPlainTime = (obj) => isType(obj, 'Temporal.PlainTime') || (isDefined(globalThis.Temporal?.PlainTime) && obj instanceof globalThis.Temporal.PlainTime) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.PlainTime') || (isDefined(obj) && isFunction(obj.toPlainDateTime) && isUndefined(obj.daysInMonth));
export const isPlainDateTime = (obj) => isType(obj, 'Temporal.PlainDateTime') || (isDefined(globalThis.Temporal?.PlainDateTime) && obj instanceof globalThis.Temporal.PlainDateTime) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.PlainDateTime') || (isDefined(obj) && isFunction(obj.toZonedDateTime) && isUndefined(obj.timeZoneId) && isUndefined(obj.timeZone) && (isDefined(obj.hour) || isDefined(obj.minute) || isDefined(obj.second) || isDefined(obj.nanosecond)));
export const isDuration = (obj) => isType(obj, 'Temporal.Duration') || (isDefined(globalThis.Temporal?.Duration) && obj instanceof globalThis.Temporal.Duration) || (isDefined(obj) && obj[Symbol.toStringTag] === 'Temporal.Duration');
export const isDurationLike = (obj) => isString(obj) || isDuration(obj) || (isObject(obj) && ('years' in obj || 'months' in obj || 'weeks' in obj || 'days' in obj ||
    'hours' in obj || 'minutes' in obj || 'seconds' in obj ||
    'milliseconds' in obj || 'microseconds' in obj || 'nanoseconds' in obj ||
    'yy' in obj || 'mm' in obj || 'ww' in obj || 'dd' in obj ||
    'hh' in obj || 'mi' in obj || 'ss' in obj ||
    'ms' in obj || 'us' in obj || 'ns' in obj));
export const isZonedDateTimeLike = (obj) => isString(obj) || isZonedDateTime(obj) || (isObject(obj) && ('year' in obj || 'month' in obj || 'day' in obj || 'hour' in obj || 'minute' in obj || 'second' in obj ||
    'millisecond' in obj || 'microsecond' in obj || 'nanosecond' in obj || 'monthCode' in obj || 'offset' in obj || 'timeZone' in obj || 'calendar' in obj));
export const isPlainYearMonth = (obj) => isType(obj, 'Temporal.PlainYearMonth') || (isDefined(globalThis.Temporal?.PlainYearMonth) && obj instanceof globalThis.Temporal.PlainYearMonth);
export const isPlainMonthDay = (obj) => isType(obj, 'Temporal.PlainMonthDay') || (isDefined(globalThis.Temporal?.PlainMonthDay) && obj instanceof globalThis.Temporal.PlainMonthDay);
// non-standard Objects
export const isEnum = (obj) => isType(obj, 'Enumify');
export const isPledge = (obj) => isType(obj, 'Pledge');
/** assert value for secure() */
export const isExtensible = (obj) => isDefined(obj?.[sym.$Extensible]);
export const isTarget = (obj) => isDefined(obj?.[sym.$Target]);
/**
 * Checks if a value is effectively empty.
 * Returns true for nullish values, empty objects, empty strings, NaN, empty arrays,
 * empty sets/maps, empty typed arrays/buffers, and invalid dates.
 *
 * @param obj - The value to check
 * @returns True if the value is empty
 * @example
 * ```ts
 * isEmpty([]); // true
 * isEmpty({ a: 1 }); // false
 * ```
 */
export const isEmpty = (obj) => false
    || isNullish(obj)
    || (isObject(obj) && Reflect.ownKeys(obj).length === 0)
    || (isString(obj) && obj.trim().length === 0)
    || (typeof obj === 'number' && Number.isNaN(obj))
    || (isArray(obj) && obj.length === 0)
    || (isSet(obj) && obj.size === 0)
    || (isMap(obj) && obj.size === 0)
    || (ArrayBuffer.isView(obj) && obj.byteLength === 0)
    || (isDate(obj) && Number.isNaN(obj.getTime()));
/**
 * Asserts a condition is true, otherwise throws an Error.
 *
 * @param condition - The boolean condition that must be true
 * @param message - The error message to throw if the condition is false
 * @throws {Error} If the condition evaluates to false
 * @example
 * ```ts
 * assertCondition(user.isLoggedIn, 'User must be logged in');
 * ```
 */
export function assertCondition(condition, message) {
    if (!condition)
        throw new Error(message);
}
/**
 * Asserts a value is a string, otherwise throws an Error.
 *
 * @param str - The value to assert as a string
 * @throws {Error} If the value is not a string
 */
export function assertString(str) { assertCondition(isString(str), `Invalid string: ${str}`); }
;
/**
 * A TypeScript exhaustiveness check that throws an error if reached at runtime.
 *
 * @param val - The value that should never exist
 * @throws {Error} Always throws an error
 */
export function assertNever(val) { throw new Error(`Unexpected object: ${val}`); }
;
